/**
 * Life4Earth V2 — React hook to fetch a rhythm's composed details
 * starting from a role definition GUID.
 *
 * DEV-1164. Wraps the by-definition shortcut endpoint
 * (GET /api/rolerhythms/by-definition/{definitionGuid}) so the caller
 * doesn't need an intermediate role-definition fetch.
 *
 * The Engagements role-detail right pane has sourceDefinitionGuid
 * directly on the selected role slot. This hook resolves the rhythm
 * in one trip and returns the composed view.
 *
 * Passing a nullish definitionGuid is the "idle" state — the hook
 * returns empty result without firing a fetch. Safe to mount
 * unconditionally and let the caller pass null when no relevant role
 * is selected.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchRhythmDetailsForDefinition } from "./roleRhythms";
import type { RhythmDetailsResponse } from "./roleRhythms";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiRoleRhythmDetailsForDefinitionResult {
  details: RhythmDetailsResponse | null;
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading" | "idle";
  refetch: () => void;
}

function cacheKeyFor(definitionGuid: string): string {
  return `roleRhythm:byDefinition:${definitionGuid.toLowerCase()}`;
}

export function useApiRoleRhythmDetailsForDefinition(
  definitionGuid: string | null | undefined,
): UseApiRoleRhythmDetailsForDefinitionResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiRoleRhythmDetailsForDefinitionResult>({
    details: null,
    loading: !!definitionGuid,
    error: null,
    source: definitionGuid ? "loading" : "idle",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!definitionGuid) {
      setData({
        details: null,
        loading: false,
        error: null,
        source: "idle",
        refetch,
      });
      return;
    }
    try {
      const details = await fetchForKey<RhythmDetailsResponse | null>(
        cacheKeyFor(definitionGuid),
        () => fetchRhythmDetailsForDefinition(definitionGuid),
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        details,
        loading: false,
        error: null,
        source: details ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        details: null,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, [definitionGuid]);

  useEffect(() => {
    mountedRef.current = true;
    void refetch();

    const onCacheUpdate = (e: Event) => {
      if (!definitionGuid) return;
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === cacheKeyFor(definitionGuid)) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [refetch, definitionGuid]);

  return data;
}
